import React from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, ColorDot, Flex, Gutter } from '@gaiads/telia-react-component-library';
import { LocalProperty, LocalPropertyValue } from '@cdaas/api-clients/dist/product-service-openapi';
import { Card } from '@cdaas/reusable-components-daas';

interface PropertyItemProps {
  readonly property: LocalProperty;
}

const typeLabel: Record<string, string> = {
  color: 'properties.types.color',
  text: 'properties.types.text',
  number: 'properties.types.number',
};

const ColorPropertyValue: React.FC<{ readonly value: LocalPropertyValue }> = ({ value }) => {
  const colorName = value.value ?? '';
  const hexCodeRaw = value.internal_value ?? '';
  const hexCode = hexCodeRaw.startsWith('#') ? hexCodeRaw : `#${hexCodeRaw}`;
  const isWhite = colorName.toLowerCase() === 'white' || ['#ffffff', '#fff'].includes(hexCode.toLowerCase());

  const labelText = value.internal_value ? `${value.value} (${value.internal_value})` : value.value;

  return (
    <Flex centeredVertically margin={{ right: 'sm', bottom: 'xs' }}>
      {hexCodeRaw && (
        <>
          <ColorDot color={hexCode} withBorder={isWhite} />
          <Gutter size="xs" />
        </>
      )}
      <BodyText color="gray700">{labelText}</BodyText>
    </Flex>
  );
};

const TextPropertyValue: React.FC<{ readonly value: LocalPropertyValue }> = ({ value }) => (
  <Flex centeredVertically>
    <BodyText color="gray700">{value.value}</BodyText>
    <Gutter size="xs" />
  </Flex>
);

export default function PropertyItem({ property }: PropertyItemProps) {
  const { t } = useTranslation('adminSettings');

  const propertyValues = property.property_values ?? [];
  const hasValues = propertyValues.length > 0;
  const isColor = property.type === 'color';
  const sortedColorValues = [...propertyValues].sort((a, b) => (a.value ?? '').localeCompare(b.value ?? '', undefined, { sensitivity: 'base' }));
  const resolvedTypeLabel = t(typeLabel[property.type] ?? property.type);

  return (
    <div data-testid="property-item">
      <Card title={property.name} alwaysOpenNoChevron>
        <BodyText color="gray700">
          {t('properties.typeLabel')}: {resolvedTypeLabel}
        </BodyText>

        {hasValues && (
          <>
            <Gutter size="sm" />
            <BodyText color="gray700" margin={{ bottom: 'xs' }}>
              {t('properties.valuesLabel')}:
            </BodyText>
            {isColor ? (
              <Flex directionColumn>
                {sortedColorValues.map(pv => (
                  <ColorPropertyValue key={pv.id} value={pv} />
                ))}
              </Flex>
            ) : (
              <Flex wrapContent={true}>
                {propertyValues.map(pv => (
                  <TextPropertyValue key={pv.id} value={pv} />
                ))}
              </Flex>
            )}
          </>
        )}

        {!hasValues && (
          <>
            <Gutter size="sm" />
            <BodyText color="gray500">{t('properties.noValues')}</BodyText>
          </>
        )}
      </Card>
    </div>
  );
}
